import React, { useState, useEffect, useRef } from 'react';
import { 
  Archive, ChevronRight, Play, Loader2, Settings2, History, 
  Trash2, RotateCcw, CheckCircle2, XCircle, Calendar,
  Clock, ShieldCheck, Power, RefreshCw, AlertTriangle
} from 'lucide-react';

import SharedSSHUnlock from './SharedSSHUnlock';
import { updateBackupConfig } from '../services/clientService';

const BackupTab = ({ tenant, isVisible, sshSessionId, socket, sshStatus, sshError, onConnect, onDisconnect, onTenantUpdate, isLeftPanelOpen, onToggleLeftPanel }) => {
  // State
  const [backups, setBackups] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Progress states
  const [isOperationRunning, setIsOperationRunning] = useState(false);
  const [operationType, setOperationType] = useState(null); // 'backup' or 'restore'
  const [progressStage, setProgressStage] = useState('');
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);

  // Auto Backup states (initialized from tenant model)
  const [autoEnabled, setAutoEnabled] = useState(tenant?.autoBackup || false);
  const [schedule, setSchedule] = useState(tenant?.backupSchedule || '02:00');
  const [retention, setRetention] = useState(tenant?.backupRetention || 7);
  const [lastAutoStatus, setLastAutoStatus] = useState(null);

  // Modals, Inputs & Errors
  const [operationError, setOperationError] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(null); // backup object
  const [editScheduleMode, setEditScheduleMode] = useState(false);
  const [tempSchedule, setTempSchedule] = useState(tenant?.backupSchedule || '02:00');

  const prevIsVisible = useRef(isVisible);
  const sshSessionIdRef = useRef(sshSessionId);

  useEffect(() => {
    sshSessionIdRef.current = sshSessionId;
  }, [sshSessionId]);

  // Sync state when tenant prop updates from database
  useEffect(() => {
    if (tenant) {
      if (typeof tenant.autoBackup !== 'undefined') setAutoEnabled(tenant.autoBackup);
      if (tenant.backupSchedule) {
        let formattedTime = tenant.backupSchedule;
        if (formattedTime.includes(' ')) {
          const parts = formattedTime.trim().split(/\s+/);
          if (parts.length >= 2) {
            const mm = parts[0].padStart(2, '0');
            const hh = parts[1].padStart(2, '0');
            formattedTime = `${hh}:${mm}`;
          }
        }
        setSchedule(formattedTime);
        setTempSchedule(formattedTime);
      }
      if (typeof tenant.backupRetention !== 'undefined') setRetention(tenant.backupRetention);
    }
  }, [tenant?._id, tenant?.DbName]);

  // Auto-dismiss transient errors after 6 seconds
  useEffect(() => {
    if (operationError) {
      const timer = setTimeout(() => {
        setOperationError(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [operationError]);

  // Auto-dismiss success banner after 5 seconds
  useEffect(() => {
    if (!isOperationRunning && progressPercent === 100) {
      const timer = setTimeout(() => {
        setProgressPercent(0);
        setShowRestoreModal(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOperationRunning, progressPercent]);

  // Auto-fetch backups list whenever tab becomes visible and SSH is active
  useEffect(() => {
    const activeSid = sshSessionIdRef.current || sshSessionId;
    if (isVisible && socket && activeSid) {
      setLoadingHistory(true);
      socket.emit('ssh:listBackups', { sessionId: activeSid, dbName: tenant?.DbName });
    }
  }, [isVisible, socket, sshSessionId, tenant?.DbName]);

  useEffect(() => {
    if (!socket) return;

    const onBackupList = (res) => {
      setLoadingHistory(false);
      if (res.success) {
        // Sort backups newest-first (latest on top)
        const sorted = (res.backups || []).slice().reverse();
        setBackups(sorted);
      } else if (res.error && res.error.includes('Non connecté')) {
        if (onDisconnect) onDisconnect();
      }
    };

    const onBackupProgress = (res) => {
      setIsOperationRunning(true);
      setOperationType('backup');
      setProgressStage(res.stage);
      setProgressMessage(res.message);
      setProgressPercent(res.percent || 0);
    };

    const onBackupComplete = (res) => {
      setIsOperationRunning(false);
      setProgressPercent(100);
      if (res && res.success === false) {
        setOperationError(res.error || 'Erreur lors de la sauvegarde');
        if (res.error && res.error.includes('Non connecté')) {
          if (onDisconnect) onDisconnect();
        }
      } else {
        setOperationError(null);
        // Refresh backup list with current session id
        const currentSid = sshSessionIdRef.current || sshSessionId;
        if (socket && currentSid) {
          setLoadingHistory(true);
          socket.emit('ssh:listBackups', { sessionId: currentSid, dbName: tenant?.DbName });
        }
      }
    };

    const onRestoreProgress = (res) => {
      setIsOperationRunning(true);
      setOperationType('restore');
      setProgressStage(res.stage);
      setProgressMessage(res.message);
      setProgressPercent(res.percent || 0);
    };

    const onRestoreComplete = (res) => {
      setIsOperationRunning(false);
      if (res && res.success === false) {
        setOperationError(res.error || 'Erreur lors de la restauration');
        if (res.error && res.error.includes('Non connecté')) {
          if (onDisconnect) onDisconnect();
        }
      } else {
        setProgressPercent(100);
        setOperationError(null);
        const currentSid = sshSessionIdRef.current || sshSessionId;
        if (socket && currentSid) {
          setLoadingHistory(true);
          socket.emit('ssh:listBackups', { sessionId: currentSid, dbName: tenant?.DbName });
        }
      }
    };

    const onBackupDeleted = () => {
      const currentSid = sshSessionIdRef.current || sshSessionId;
      if (socket && currentSid) {
        setLoadingHistory(true);
        socket.emit('ssh:listBackups', { sessionId: currentSid, dbName: tenant?.DbName });
      }
    };

    const onAutoBackupToggled = (res) => {
      if (res && res.success) {
        if (typeof res.enable !== 'undefined') setAutoEnabled(res.enable);
        if (onTenantUpdate) onTenantUpdate();
      } else if (res && res.error) {
        setOperationError(res.error);
        if (tenant && typeof tenant.autoBackup !== 'undefined') {
          setAutoEnabled(tenant.autoBackup);
        }
      }
    };

    const onScheduleUpdated = (res) => {
      if (res && res.success) {
        if (onTenantUpdate) onTenantUpdate();
      } else if (res && res.error) {
        setOperationError(res.error);
      }
    };

    socket.on('ssh:backupList', onBackupList);
    socket.on('ssh:backupProgress', onBackupProgress);
    socket.on('ssh:backupComplete', onBackupComplete);
    socket.on('ssh:restoreProgress', onRestoreProgress);
    socket.on('ssh:restoreComplete', onRestoreComplete);
    socket.on('ssh:backupDeleted', onBackupDeleted);
    socket.on('ssh:autoBackupToggled', onAutoBackupToggled);
    socket.on('ssh:scheduleUpdated', onScheduleUpdated);

    return () => {
      socket.off('ssh:backupList', onBackupList);
      socket.off('ssh:backupProgress', onBackupProgress);
      socket.off('ssh:backupComplete', onBackupComplete);
      socket.off('ssh:restoreProgress', onRestoreProgress);
      socket.off('ssh:restoreComplete', onRestoreComplete);
      socket.off('ssh:backupDeleted', onBackupDeleted);
      socket.off('ssh:autoBackupToggled', onAutoBackupToggled);
      socket.off('ssh:scheduleUpdated', onScheduleUpdated);
    };
  }, [socket, tenant?.DbName]);

  const fetchBackups = () => {
    const activeSid = sshSessionIdRef.current || sshSessionId;
    if (!socket || !activeSid) return;
    setLoadingHistory(true);
    socket.emit('ssh:listBackups', { sessionId: activeSid, dbName: tenant?.DbName });
  };

  const triggerBackup = () => {
    if (!socket || !sshSessionId || isOperationRunning) return;
    setIsOperationRunning(true);
    setOperationType('backup');
    setProgressPercent(0);
    setProgressStage('init');
    setProgressMessage('Initialisation...');
    socket.emit('ssh:triggerBackup', { sessionId: sshSessionId, dbName: tenant?.DbName });
  };

  const triggerRestore = (filename) => {
    if (!socket || !sshSessionId || isOperationRunning) return;
    setIsOperationRunning(true);
    setOperationType('restore');
    setProgressPercent(0);
    setProgressStage('init');
    setProgressMessage('Préparation...');
    socket.emit('ssh:restoreBackup', { sessionId: sshSessionId, dbName: tenant?.DbName, filename });
  };

  const triggerDelete = (filename) => {
    if (!socket || !sshSessionId || isOperationRunning) return;
    socket.emit('ssh:deleteBackup', { sessionId: sshSessionId, dbName: tenant?.DbName, filename });
    // Optimistic remove
    setBackups(prev => prev.filter(b => b.filename !== filename));
  };

  const toggleAutoBackup = async (enable) => {
    setAutoEnabled(enable);
    try {
      if (tenant?._id) {
        await updateBackupConfig(tenant._id, {
          autoBackup: enable,
          backupSchedule: tempSchedule || schedule || '02:00',
          backupRetention: retention,
          sshSessionId
        });
        if (onTenantUpdate) onTenantUpdate();
      }
    } catch (err) {
      console.error("❌ HTTP Backup Config Update Error:", err);
    }
  };

  const updateSchedule = async () => {
    const newSched = tempSchedule || '02:00';
    setSchedule(newSched);
    setTempSchedule(newSched);
    setEditScheduleMode(false);
    try {
      if (tenant?._id) {
        await updateBackupConfig(tenant._id, {
          autoBackup: autoEnabled,
          backupSchedule: newSched,
          backupRetention: retention,
          sshSessionId
        });
        if (onTenantUpdate) onTenantUpdate();
      }
    } catch (err) {
      console.error("❌ HTTP Schedule Update Error:", err);
    }
  };

  const updateRetention = async (newRent) => {
    const rent = Math.max(1, newRent);
    setRetention(rent);
    try {
      if (tenant?._id) {
        await updateBackupConfig(tenant._id, {
          autoBackup: autoEnabled,
          backupSchedule: tempSchedule || schedule || '02:00',
          backupRetention: rent,
          sshSessionId
        });
        if (onTenantUpdate) onTenantUpdate();
      }
    } catch (err) {
      console.error("❌ HTTP Retention Update Error:", err);
    }
  };

  // Format size helper
  const formatSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '—';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format backup date helper with clean HH:MM:SS display
  const formatBackupDate = (backup) => {
    if (backup.createdAt) {
      try {
        const d = new Date(backup.createdAt);
        if (!isNaN(d.getTime())) {
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} à ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
      } catch (e) {}
    }
    if (backup.filename) {
      const match = backup.filename.match(/backup_(\d{4}-\d{2}-\d{2})_(\d{2})[-:]?(\d{2})[-:]?(\d{2})/);
      if (match) {
        return `${match[1]} à ${match[2]}:${match[3]}:${match[4]}`;
      }
    }
    return backup.date || '—';
  };

  // Map progress stage to message
  const getStageMessage = (stage, msg) => {
    if (msg) return msg;
    const map = {
      'init': 'Initialisation...',
      'database': 'Sauvegarde de la base de données...',
      'uploads': 'Archivage des fichiers uploads...',
      'archive': 'Création de l\'archive finale...',
      'cleanup': 'Nettoyage...',
      'complete': 'Sauvegarde terminée !',
      'extract': 'Extraction de l\'archive...',
      'restart': 'Redémarrage du backend...'
    };
    return map[stage] || stage || 'En cours...';
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-100 overflow-hidden relative">
      {/* Top Header Bar */}
      <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
        <div className="flex items-center gap-3">
          {!isLeftPanelOpen && (
            <button
              onClick={onToggleLeftPanel}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
              title="Déplier Fiche Instance"
            >
              <ChevronRight size={18} />
            </button>
          )}
          <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100">
            <Archive size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Sauvegardes & Restauration</h3>
            </div>
            <div className="text-xs font-medium flex items-center gap-1.5 mt-0.5 text-slate-500">
              {backups.length} sauvegarde{backups.length !== 1 ? 's' : ''}
              {backups.length > 0 && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>Dernière: {formatBackupDate(backups[0])}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {sshStatus === 'connected' && (
            <>
              <button
                onClick={fetchBackups}
                disabled={loadingHistory}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 hover:text-emerald-700 bg-slate-50 hover:bg-emerald-50 border border-slate-200 rounded-lg transition-all"
                title="Actualiser l'historique"
              >
                <RefreshCw size={15} className={loadingHistory ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
              <button
                onClick={() => {
                  if (onDisconnect) {
                    onDisconnect();
                  } else if (tenant?.id) {
                    sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
                    if (socket && sshSessionId) socket.emit('ssh:disconnect', { sessionId: sshSessionId });
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-all shadow-xs"
                title="Déconnecter le SSH"
              >
                <Power size={14} />
                <span>Déconnecter</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 flex overflow-hidden relative">
        {sshStatus !== 'connected' || !sshSessionId ? (
          <SharedSSHUnlock 
            tenant={tenant} 
            status={sshStatus} 
            errorMessage={sshError} 
            onConnect={onConnect} 
            title="Accès Sauvegardes"
            description="Connectez-vous via SSH pour gérer les sauvegardes complètes (fichiers et base de données) de cette instance."
          />
        ) : (
          <div className="flex-1 overflow-y-auto p-4 lg:p-8">
            <div className="max-w-5xl mx-auto space-y-6">
              
              {/* Transient Error Banner (dismissible) */}
              {operationError && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex items-start justify-between gap-3 text-rose-800 shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="text-rose-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-rose-700">Échec de l'opération</h4>
                      <p className="text-sm mt-1 font-mono text-rose-900">{operationError}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setOperationError(null)} 
                    className="px-3 py-1 bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-bold rounded-lg transition-colors shrink-0"
                  >
                    Fermer
                  </button>
                </div>
              )}

              {/* Top Row: Actions & Auto Backup */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Section 1: Actions Rapides */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)] flex flex-col">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                    <Play size={12} className="text-emerald-500" />
                    Actions Rapides
                  </h4>
                  
                  <div className="flex-1 flex flex-col items-center justify-center py-4">
                    <button
                      onClick={triggerBackup}
                      disabled={isOperationRunning}
                      className={`relative overflow-hidden group w-full max-w-sm flex items-center justify-center gap-3 py-4 rounded-xl text-white font-bold transition-all ${
                        isOperationRunning 
                          ? 'bg-slate-300 cursor-not-allowed'
                          : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 shadow-md hover:shadow-lg'
                      }`}
                    >
                      {isOperationRunning && operationType === 'backup' ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          <span>Sauvegarde en cours...</span>
                        </>
                      ) : (
                        <>
                          <Archive size={20} className={!isOperationRunning ? "group-hover:scale-110 transition-transform" : ""} />
                          <span className="text-lg">Backup Maintenant</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-slate-400 mt-4 text-center">
                      Crée une archive complète incluant la base de données MongoDB et tous les fichiers uploads.
                    </p>
                  </div>
                </div>

                {/* Section 3: Sauvegarde Automatique */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)] flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                      <Settings2 size={12} className="text-emerald-500" />
                      Sauvegarde Automatique
                    </h4>
                    
                    <button
                      onClick={() => toggleAutoBackup(!autoEnabled)}
                      disabled={isOperationRunning}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        autoEnabled ? 'bg-emerald-500' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        autoEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>

                  <div className="space-y-4 flex-1">
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-50 rounded-lg">
                          <Clock size={16} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-700">Planification</p>
                          {editScheduleMode ? (
                            <div className="flex items-center gap-2 mt-1">
                              <input 
                                type="time" 
                                value={tempSchedule}
                                onChange={(e) => setTempSchedule(e.target.value)}
                                className="text-xs border border-slate-200 rounded px-2 py-1 outline-none focus:border-emerald-400"
                              />
                              <button onClick={updateSchedule} className="text-xs font-bold text-white bg-emerald-500 px-2 py-1 rounded hover:bg-emerald-600">OK</button>
                              <button onClick={() => setEditScheduleMode(false)} className="text-xs font-bold text-slate-500 px-2 py-1 hover:text-slate-700">Annuler</button>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-900 font-medium">Tous les jours à {tempSchedule}</p>
                          )}
                        </div>
                      </div>
                      {!editScheduleMode && (
                        <button onClick={() => setEditScheduleMode(true)} disabled={!autoEnabled} className="text-xs font-bold text-emerald-600 hover:underline disabled:opacity-50">
                          Modifier Horaire
                        </button>
                      )}
                    </div>

                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-50 rounded-lg">
                          <History size={16} className="text-slate-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-700">Rétention</p>
                          <p className="text-sm text-slate-900 font-medium">{retention} sauvegardes conservées</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateRetention(retention - 1)} disabled={!autoEnabled} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-slate-600 disabled:opacity-50 hover:bg-slate-200">-</button>
                        <button onClick={() => updateRetention(retention + 1)} disabled={!autoEnabled} className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-slate-600 disabled:opacity-50 hover:bg-slate-200">+</button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2 text-xs font-medium">
                      Status: 
                      {autoEnabled ? (
                        <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={14}/> Activée</span>
                      ) : (
                        <span className="text-slate-500 flex items-center gap-1"><XCircle size={14}/> Désactivée</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Inline Restore Confirmation Card (on page) */}
              {showRestoreModal && !isOperationRunning && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4 animate-in fade-in duration-200">
                  <div className="flex items-start gap-3.5">
                    <div className="p-3 bg-amber-100 text-amber-700 rounded-xl shrink-0 mt-0.5">
                      <AlertTriangle size={22} />
                    </div>
                    <div>
                      <h4 className="text-sm font-extrabold text-amber-950">Confirmer la restauration de : <span className="font-mono bg-white px-2 py-0.5 rounded border border-amber-300 text-slate-900">{showRestoreModal.filename}</span></h4>
                      <p className="text-xs text-amber-800 font-medium mt-1">⚠️ Attention : La base de données MongoDB et tous les fichiers uploads seront remplacés par cette sauvegarde.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0 self-end md:self-center">
                    <button 
                      onClick={() => setShowRestoreModal(null)}
                      className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all shadow-xs"
                    >
                      Annuler
                    </button>
                    <button 
                      onClick={() => triggerRestore(showRestoreModal.filename)}
                      className="px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 rounded-xl shadow-md hover:shadow-lg transition-all flex items-center gap-1.5"
                    >
                      <RotateCcw size={14} />
                      <span>Confirmer la Restauration</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Inline Live Progress Card (on page) */}
              {isOperationRunning && (
                <div className="bg-white border-2 border-emerald-300/80 rounded-2xl p-6 shadow-[0_4px_24px_rgba(16,185,129,0.14)] relative overflow-hidden animate-in fade-in duration-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                        <Loader2 size={22} className="animate-spin text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="text-base font-extrabold text-slate-900">
                          {operationType === 'backup' ? 'Création de la sauvegarde en cours...' : 'Restauration de l\'instance en cours...'}
                        </h4>
                        <p className="text-xs text-slate-500 font-semibold mt-0.5">{getStageMessage(progressStage, progressMessage)}</p>
                      </div>
                    </div>
                    <span className="text-2xl font-black text-emerald-600 font-mono tracking-tight">{progressPercent}%</span>
                  </div>

                  <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden p-0.5 border border-slate-200">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Inline Operation Complete Banner */}
              {!isOperationRunning && progressPercent === 100 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between text-emerald-900 shadow-xs animate-in fade-in duration-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-800">Opération terminée avec succès !</h4>
                      <p className="text-xs font-medium text-emerald-700">L'opération a été exécutée correctement.</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      setProgressPercent(0);
                      setShowRestoreModal(null);
                    }}
                    className="px-3 py-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold rounded-lg transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              )}

              {/* Section 4: Historique des Sauvegardes */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                    <History size={12} className="text-emerald-500" />
                    Historique des Sauvegardes
                  </h4>
                </div>

                {loadingHistory ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Loader2 size={24} className="animate-spin mb-2 text-emerald-500" />
                    <span className="text-sm font-medium">Chargement de l'historique...</span>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-slate-100 rounded-xl bg-slate-50 text-slate-400">
                    <Archive size={32} className="mb-3 text-slate-300" />
                    <span className="text-sm font-bold text-slate-600">Aucune sauvegarde trouvée</span>
                    <span className="text-xs mt-1">Créez votre première sauvegarde dès maintenant.</span>
                  </div>
                ) : (
                  <div className="overflow-hidden border border-slate-200 rounded-xl">
                    <table className="w-full text-left border-collapse whitespace-nowrap">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Fichier</th>
                          <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Type</th>
                          <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Date</th>
                          <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600">Taille</th>
                          <th className="py-3 px-4 text-xs font-black uppercase tracking-wider text-slate-600 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-xs">
                        {backups.map((backup, i) => (
                          <tr key={i} className="hover:bg-slate-50 transition-colors">
                            <td className="py-3 px-4 font-mono font-medium text-slate-700">
                              <div className="flex items-center gap-2">
                                <Archive size={14} className="text-emerald-500" />
                                {backup.filename}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {backup.type === 'auto' ? (
                                <span className="px-2 py-0.5 rounded text-[10px] font-black bg-blue-50 text-blue-700 uppercase">Auto</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-[10px] font-black bg-slate-100 text-slate-600 uppercase">Manuel</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-slate-600 font-medium">
                              {formatBackupDate(backup)}
                            </td>
                            <td className="py-3 px-4 font-mono text-slate-600 font-semibold">
                              {formatSize(backup.size)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setShowRestoreModal(backup)}
                                  disabled={isOperationRunning}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <RotateCcw size={13} />
                                  Restaurer
                                </button>
                                <button
                                  onClick={() => triggerDelete(backup.filename)}
                                  disabled={isOperationRunning}
                                  className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Supprimer"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default React.memo(BackupTab);
